import React, { useState, useEffect } from "react";
import styles from "./css/Registration.module.css";
import { IconButton, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import sendEmail from "../../emailutil";

interface ValidationErrors {
  username?: string;
  email?: string;
  password?: string;
}

const Registration = () => {
  const navigate = useNavigate();
  const language = useSelector((state: any) => state.language.value);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState({
    username: false,
    email: false,
    password: false,
  });

  // Password requirements
  const passwordRequirements = {
    minLength: password.length >= 8,
    hasLetter: /[a-zA-Z]/.test(password),
    hasNumber: /\d/.test(password),
  };

  const validateField = (field: string, value: string) => {
    const newErrors: ValidationErrors = { ...errors };

    switch (field) {
      case "username":
        if (!value) {
          newErrors.username = "error";
        } else if (value.length < 3) {
          newErrors.username = "error";
        } else {
          delete newErrors.username;
        }
        break;

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) {
          newErrors.email = "error";
        } else if (!emailRegex.test(value)) {
          newErrors.email = "error";
        } else {
          delete newErrors.email;
        }
        break;

      case "password":
        if (!value) {
          newErrors.password = "error";
        } else if (!Object.values(passwordRequirements).every(Boolean)) {
          newErrors.password = "error";
        } else {
          delete newErrors.password;
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
    validateField(
      field,
      field === "username" ? username : field === "email" ? email : password
    );
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const notify = (message: string) => {
    toast(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
    });
  };

  const sendConfirmationEmails = async (userData: {
    username: string;
    email: string;
  }) => {
    try {
      // Email to user
      const userEmailSubject =
        language === "RO"
          ? "Bine ați venit la Spirit Liber!"
          : "Welcome to Spirit Liber!";

      const userEmailHtml =
        language === "RO"
          ? `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Bine ați venit la Spirit Liber, ${userData.username}!</h2>
            <p>Vă mulțumim pentru înregistrare. Contul dumneavoastră a fost creat cu succes.</p>
            <p>Puteți acum să:</p>
            <ul>
              <li>Cumpărați cărți în format digital sau fizic</li>
              <li>Accesați cărțile audio</li>
              <li>Gestionați comenzile dumneavoastră</li>
            </ul>
            <p>Pentru orice întrebări, nu ezitați să ne contactați.</p>
            <p>Cu stimă,<br>Echipa Spirit Liber</p>
          </div>
        `
          : `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Welcome to Spirit Liber, ${userData.username}!</h2>
            <p>Thank you for registering. Your account has been successfully created.</p>
            <p>You can now:</p>
            <ul>
              <li>Purchase books in digital or physical format</li>
              <li>Access audio books</li>
              <li>Manage your orders</li>
            </ul>
            <p>If you have any questions, don't hesitate to contact us.</p>
            <p>Best regards,<br>Spirit Liber Team</p>
          </div>
        `;

      // Send welcome email to user
      await sendEmail({
        userEmail: userData.email, // This is required - use the new user's email
        subject: userEmailSubject, // This is required
        message: userEmailHtml, // This is required
        itemName: "Registration Confirmation",
        recepient: userData.email,
      });

      // Send notification to owner
      const ownerEmailSubject =
        language === "RO"
          ? "Nou utilizator înregistrat"
          : "New user registered";

      const ownerEmailHtml =
        language === "RO"
          ? `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Nou utilizator înregistrat</h2>
            <p>Un nou utilizator s-a înregistrat pe platforma Spirit Liber.</p>
            <p><strong>Detalii:</strong></p>
            <ul>
              <li>Nume utilizator: ${userData.username}</li>
              <li>Email: ${userData.email}</li>
              <li>Data înregistrării: ${new Date().toLocaleString("ro-RO")}</li>
            </ul>
          </div>
        `
          : `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>New User Registration</h2>
            <p>A new user has registered on the Spirit Liber platform.</p>
            <p><strong>Details:</strong></p>
            <ul>
              <li>Username: ${userData.username}</li>
              <li>Email: ${userData.email}</li>
              <li>Registration date: ${new Date().toLocaleString("en-US")}</li>
            </ul>
          </div>
        `;

      await sendEmail({
        userEmail: userData.email, // This is required - use the new user's email
        subject: ownerEmailSubject, // This is required
        message: ownerEmailHtml, // This is required
        itemName: "New User Registration",
        recepient: process.env.REACT_APP_OWNER_EMAIL || "",
      });
    } catch (error) {
      console.error("Error sending confirmation emails:", error);
    }
  };

  const handleRegister = async () => {
    // Validate all fields
    const isUsernameValid = validateField("username", username);
    const isEmailValid = validateField("email", email);
    const isPasswordValid = validateField("password", password);

    if (!isUsernameValid || !isEmailValid || !isPasswordValid) {
      setTouched({ username: true, email: true, password: true });

      if (errors.username) {
        notify(
          language === "RO"
            ? "Numele de utilizator este obligatoriu"
            : "Username is required"
        );
      }
      if (errors.email) {
        notify(
          language === "RO" ? "Email-ul este obligatoriu" : "Email is required"
        );
      }
      if (errors.password) {
        notify(
          language === "RO"
            ? "Parola nu îndeplinește toate cerințele"
            : "Password does not meet all requirements"
        );
      }
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/auth/register`,
        { username, email, password }
      );

      if (response.status === 201) {
        try {
          // Send confirmation emails and wait for them to complete
          await sendConfirmationEmails({ username, email });

          // Only notify and navigate after emails are sent
          notify(
            language === "RO"
              ? "Înregistrare reușită! Vă rugăm să verificați email-ul. Redirecționare către pagina de autentificare..."
              : "Registration successful! Please check your email. Redirecting to login..."
          );
          setTimeout(() => navigate("/login"), 2000);
        } catch (emailError) {
          console.error("Error sending confirmation emails:", emailError);
          // Still notify of successful registration but mention email issue
          notify(
            language === "RO"
              ? "Înregistrare reușită! Dar a apărut o problemă la trimiterea email-ului de confirmare."
              : "Registration successful! But there was an issue sending the confirmation email."
          );
          setTimeout(() => navigate("/login"), 2000);
        }
      }
    } catch (err: any) {
      if (err.response?.data?.message) {
        const serverMessage = err.response.data.message;

        // Handle specific error messages from the backend
        if (serverMessage === "Email already in use") {
          notify(
            language === "RO"
              ? "Acest email este deja înregistrat"
              : "Email already in use"
          );
        } else if (serverMessage === "User registered successfully") {
          notify(
            language === "RO"
              ? "Înregistrare reușită! Redirecționare către pagina de autentificare..."
              : "Registration successful! Redirecting to login..."
          );
          setTimeout(() => navigate("/login"), 2000);
        } else if (serverMessage.includes("validation")) {
          notify(
            language === "RO"
              ? "Vă rugăm să verificați datele introduse"
              : "Please check your input data"
          );
        } else {
          // Generic error message for unhandled cases
          notify(
            language === "RO"
              ? "A apărut o eroare. Vă rugăm să încercați din nou."
              : "An error occurred. Please try again."
          );
        }
      } else {
        notify(
          language === "RO"
            ? "Conexiune refuzată. Vă rugăm încercați mai târziu."
            : "Connection refused. Please try again later."
        );
      }
    }
  };

  return (
    <div className={styles.productContainer}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className={styles.productInfo}>
        <div className={styles.frame}>
          <span className={styles.productTitle7}>
            {language === "RO" ? "Înregistrare" : "Sign up"}
          </span>
          <div className={styles.form}>
            <div className={styles.inputs}>
              <div className={styles.inputField}>
                <TextField
                  sx={{
                    width: "100%",
                    height: "inherit",
                    "& .MuiFilledInput-root": {
                      height: "inherit",
                      border:
                        touched.username && errors.username
                          ? "1px solid #ff0000"
                          : "none",
                    },
                  }}
                  error={touched.username && !!errors.username}
                  hiddenLabel
                  id="username"
                  placeholder={
                    language === "RO" ? "Nume utilizator" : "Username"
                  }
                  variant="filled"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (touched.username)
                      validateField("username", e.target.value);
                  }}
                  onBlur={() => handleBlur("username")}
                />
              </div>
            </div>

            <div className={styles.inputField}>
              <TextField
                sx={{
                  width: "100%",
                  "& .MuiFilledInput-root": {
                    height: "inherit",
                    border:
                      touched.email && errors.email
                        ? "1px solid #ff0000"
                        : "none",
                  },
                }}
                error={touched.email && !!errors.email}
                hiddenLabel
                id="email"
                placeholder="Email"
                variant="filled"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (touched.email) validateField("email", e.target.value);
                }}
                onBlur={() => handleBlur("email")}
              />
            </div>

            <div className={styles.inputField}>
              <TextField
                sx={{
                  width: "100%",
                  "& .MuiFilledInput-root": {
                    height: "inherit",
                    border:
                      touched.password && errors.password
                        ? "1px solid #ff0000"
                        : "none",
                  },
                }}
                error={touched.password && !!errors.password}
                hiddenLabel
                type={showPassword ? "text" : "password"}
                id="filled-hidden-label-normal"
                placeholder="Password"
                variant="filled"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (touched.password)
                    validateField("password", e.target.value);
                }}
                onBlur={() => handleBlur("password")}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleClickShowPassword}
                      edge="end"
                      sx={{ paddingRight: "10px" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </div>
          </div>
          {touched.username && errors.username && (
            <div
              style={{
                color: "#ff0000",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: "inherit",
              }}
            >
              {language === "RO"
                ? "Numele de utilizator trebuie să conțină cel puțin 3 caractere"
                : "Username must be at least 3 characters long"}
            </div>
          )}
          {touched.email && errors.email && (
            <div
              style={{
                color: "#ff0000",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: "inherit",
              }}
            >
              {language === "RO"
                ? "Vă rugăm să introduceți o adresă de email validă"
                : "Please enter a valid email address"}
            </div>
          )}
          {touched.password && errors.password && (
            <div
              style={{
                color: "#ff0000",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: "inherit",
              }}
            >
              {language === "RO" ? (
                <div>
                  Parola trebuie să conțină:
                  <ul style={{ margin: "4px 0 0 20px", padding: 0 }}>
                    {!passwordRequirements.minLength && (
                      <li style={{ width: "fit-content" }}>
                        Cel puțin 8 caractere
                      </li>
                    )}
                    {!passwordRequirements.hasLetter && (
                      <li style={{ width: "fit-content" }}>
                        Cel puțin o literă
                      </li>
                    )}
                    {!passwordRequirements.hasNumber && (
                      <li style={{ width: "fit-content" }}>
                        Cel puțin un număr
                      </li>
                    )}
                  </ul>
                </div>
              ) : (
                <div>
                  Password must contain:
                  <ul style={{ margin: "4px 0 0 20px", padding: 0 }}>
                    {!passwordRequirements.minLength && (
                      <li style={{ width: "fit-content" }}>
                        At least 8 characters
                      </li>
                    )}
                    {!passwordRequirements.hasLetter && (
                      <li style={{ width: "fit-content" }}>
                        At least one letter
                      </li>
                    )}
                    {!passwordRequirements.hasNumber && (
                      <li style={{ width: "fit-content" }}>
                        At least one number
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          <button
            className={styles.buttonE}
            onClick={handleRegister}
            disabled={Object.keys(errors).length > 0}
          >
            <span className={styles.addToCartF}>
              {language === "RO" ? "Înregistrare" : "Sign up"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Registration;
